import React, {useEffect, useState, useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {chunk, labels} from '../../../../../../../util/utils';
import Fetch from '../../../../../Fetch/fetch';
import {
	estimateListingPagingOptions,
	documentTypes,
	clientSideEventNames,
	formBodyType,
	ValidationGroup,
	riskCategory,
	documentCategoryNames,
	CardTypes,
	guidanceType,
	validationTypes,
	entities,
	bodyTypesWithEmbeddedDocumentTypes,
	ValidationGroup as validationGroup
} from '../../../../../../../util/uiconstants';
import {getRisks} from '../../../../../../../actions/Risk/riskactions';
import {getEntityDocuments} from '../../../../../../../actions/Document/entityDocumentActions';
import AresSpecialBodyContainer from '../../AresSpecialBodyContainer';
import Loader from '../../../../../LoadingIndicator/LoadingIndicator';
import Search from '../../../../../BaseComponentWrappers/MotifSearch';
import Pagination from '../../../../../Pagination/Pagination';
import {useDocumentId, useEngagementId} from '../../../../../../../util/customHooks';
import {getDocumentValidations} from '../../../../../../../actions/ValidationControl/validationcontrolactions';
import Button from '../../../../../BaseComponentWrappers/MotifButton';
import EstimateList from './EstimateList';
import Utility from '../../../../../../../util/utilityfunctions';
import {currentResource} from '../../../../../../../util/utils';
import {Entity} from '../../../../../../../util/uiconstants';
import {getTaskDocumentByDocumentId} from '../../../../../../../selectors/Ares/selectors';
import RelateEstimateToAccount from '../../Estimates/RelateEstimateToAccount';
import CreateEditEstimateModalWrapper from '../../../../../EntityCrud/CreateEditEstimateModalWrapper';
import {getAccountById, updateStoreAccountRelation} from '../../../../../../../actions/Account/accountactions';
import {getValidationsForDocumentId} from '../../../../../../../selectors/Forms/canvasFormSelectors';
import {useCreateEntityForm, useAresUrlParameters} from '../../../../../../../util/Ares/customHooks';
import {FormBodyTypeIdContext} from '../../../FormBodyContainer';
import ManageEstimates from '../../../../../Estimates/ManageEstimates/ManageEstimates';

export default function AccountBodyEstimateListing({
	bodyContent,
	headerId,
	sectionId,
	bodyId,
	cardTypeId,
	updateBodyOptions,
	getBodiesBySectionId,
	isSnapshotView,
	showGuidance
}) {
	//#region Variables
	const dispatch = useDispatch();

	const engagementId = useEngagementId(),
		documentId = useDocumentId(),
		engagement = useSelector((store) => store.engagement),
		estimates = useSelector((state) => state.risks),
		entityDocuments = useSelector((store) => store.entityDocuments);
	const [isLoading, setIsLoading] = useState(true),
		[searchText, setSearchText] = useState(''),
		[doSearch, setDoSearch] = useState(false),
		[isFirstCall, setIsFirstCall] = useState(true),
		[paging, setPaging] = useState({
			currentPage: 1,
			pageSize: estimateListingPagingOptions.options[0],
			totalPages: 1
		});
	const [validateShowRelateEstimateLink, setShowRelateEstimateLink] = useState(true);
	const [showRelateEstimateModal, setShowRelateEstimateModal] = useState(false);
	const [showCreateEstimateModal, setShowCreateEstimateModal] = useState(false);
	const [formattedRisks, setFormattedRisks] = useState([]);
	const currentDocument = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));

	const documentTypeId = documentTypes.ESTIMATESDOCUMENT,
		noRecordLabel = searchText && searchText.trim() !== '' ? labels.noResultsAvailable : labels.noEstimatesFound;
	const filterGuidanceTypes = (guidance) =>
		guidance.guidanceTypeId !== guidanceType.AtlasDocument &&
		guidance.guidanceTypeId !== guidanceType.OtherApplications;

	const formBodyTypeId = useContext(FormBodyTypeIdContext);
	const validations = useSelector((store) => getValidationsForDocumentId(store, documentId));
	const {taskId} = useAresUrlParameters();

	const FormBodyTypeDocumentTypeMap = {
		[formBodyType.AccountRelatedEstimateListing]: {
			documentTypeId: documentTypes.ESTIMATESDOCUMENT,
			validationTypeId: validationTypes.EstimateWithoutIndividualEstimateForm
		}
	};
	const documentTypeIdToBeCreated =
		formBodyTypeId !== undefined ? FormBodyTypeDocumentTypeMap[formBodyTypeId].documentTypeId : null;
	const validationTypeId =
		formBodyTypeId !== undefined ? FormBodyTypeDocumentTypeMap[formBodyTypeId].validationTypeId : null;

	const handleOnPageSizeChange = (pageSize) => {
		setPaging({...paging, pageSize, currentPage: 1});
		setDoSearch(true);
	};

	const handleOnPageChange = (pageNumber) => {
		setPaging({...paging, currentPage: pageNumber});
		setDoSearch(true);
	};

	const fetch = useCallback(() => {
		if (isFirstCall || (!isFirstCall && doSearch)) {
			getEstimatesForBody();
		}
	}, [engagementId, paging.currentPage, paging.pageSize, doSearch, dispatch]);

	const getDocuments = (val) => {
		dispatch(getEntityDocuments(engagementId, documentTypeId, documentCategoryNames.canvasFormBodyEvidence)).then(
			() => {
				setIsLoading(false);
				setIsFirstCall(false);
				setDoSearch(false);
				val && getValidations();
			}
		);
	};

	const getEstimatesForBody = () => {
		isFirstCall && setIsLoading(true);
		if (Utility.isValidNumber(paging.currentPage)) {
			let queryParams = {...getQueryParams()};
			if (doSearch && isValidSearch(searchText)) {
				queryParams = {...queryParams, searchString: searchText};
			}

			dispatch(getRisks(engagementId, queryParams)).then(() => {
				getDocuments(true);
			});
		}
	};

	const onCreateDocument = () => {
		getDocuments();
	};

	const onChangeSearch = (event) => {
		setSearchText(event.target.value);
	};

	const onSearch = () => {
		setPaging({...paging, currentPage: 1});
		setDoSearch(true);
	};

	const onSearchClear = () => {
		setSearchText('');
		setDoSearch(true);
		setPaging({...paging, currentPage: 1});
	};

	//#endregion

	//#region Functions
	let getQueryParams = () => {
		return {
			page: paging.currentPage,
			pageSize: paging.pageSize,
			orderBy: 'RiskName',
			includePagination: true,
			includeRelatedRisks: true,
			includeRelatedDocuments: true,
			orderByAscending: true,
			riskCategory: riskCategory.estimates,
			filterRelatedAccounts:
				currentDocument?.smartEvidenceSourceEntityID === Entity.Account ? currentDocument?.smartEvidenceSourceId : null
		};
	};

	const isValidSearch = (search) => {
		return _.trim(search) !== '' && (!_.isEmpty(_.trim(search)) || _.trim(search) === search);
	};

	const handleOnSetFilter = () => {
		//TODO: handle filter
	};

	const onDisableFilter = () => {
		//TODO: handle filter
	};

	const onHideRelateEstimates = () => {
		setShowRelateEstimateModal(!showRelateEstimateModal);
		dispatch(getAccountById(engagementId, currentDocument?.smartEvidenceSourceId));
	};

	const onCloseEditEstimatesModal = () => {
		setTimeout(() => {
			getEstimatesForBody();
			getValidations();
			dispatch(getAccountById(engagementId, currentDocument?.smartEvidenceSourceId));
		}, 700);
	};

	//#region UseEffect
	useEffect(() => {
		if (currentDocument && estimates) {
			if (
				currentDocument?.smartEvidenceSourceEntityID !== Entity.Account ||
				currentDocument?.smartEvidenceSourceId === null
			) {
				setShowRelateEstimateLink(false);
			} else if (
				currentDocument?.smartEvidenceSourceEntityID === Entity.Account &&
				currentDocument?.smartEvidenceSourceId
			) {
				setShowRelateEstimateLink(true);
			}
		}
	}, [currentDocument, estimates]);

	useEffect(() => {
		window.addEventListener(clientSideEventNames.getEstimatesForBody, getEstimatesForBody);
		return () => {
			window.removeEventListener(clientSideEventNames.getEstimatesForBody, getEstimatesForBody);
		};
	}, [getEstimatesForBody]);

	useEffect(() => {
		window.addEventListener(clientSideEventNames.estimateDocumentDeleted, estimateDocumentDeleted);
		return () => {
			window.removeEventListener(clientSideEventNames.estimateDocumentDeleted, estimateDocumentDeleted);
		};
	}, [estimateDocumentDeleted]);

	useEffect(() => {
		if (estimates && estimates?.data) {
			let formattedRisks = [];
			estimates.data.map((estimate) => {
				formattedRisks.push({id: estimate.id, name: estimate.riskName});
			});
			setFormattedRisks(formattedRisks);

			// get estimates without document and create.
			let hasMissingEstimateDoc = estimates?.data?.filter((f) => f.documents?.length === 0);
			if (hasMissingEstimateDoc?.length) {
				let estimateIds = validations?.length > 0 ? getObjectIds() : hasMissingEstimateDoc.map((m) => m.id);
				if (estimateIds?.length > 0) {
					createMissingDocuments(estimateIds);
				}
			}
		}
	}, [estimates]);

	useEffect(() => {
		let totalPages = Math.ceil(estimates?.totalEntityCount / paging.pageSize);
		setPaging({...paging, totalPages});
	}, [paging.currentPage, paging.pageSize, estimates?.totalEntityCount]);

	useEffect(() => {
		const ids = entityDocuments.allIds?.filter((id) => {
			const entityDocument = entityDocuments.byId[id];
			return (
				estimates?.data?.filter((f) => f.id === entityDocument.smartEvidenceSourceId) &&
				entityDocument.documentTypeId === documentTypes.ESTIMATESDOCUMENT
			);
		});

		const documents = ids?.map((id) => {
			return entityDocuments && entityDocuments.byId && entityDocuments.byId[id];
		});

		if (documents && documents.length > 0) {
			// push currentDocument to documents to refresh 158 validation
			// documents.push(currentDocument);
			chunk(documents, 10).forEach((chunkedDocuments) => {
				dispatch(
					getDocumentValidations(engagementId, chunkedDocuments, undefined, undefined, ValidationGroup.GuidedWorkFlow)
				);
			});
		}
	}, [entityDocuments]);

	useEffect(() => {
		if (currentDocument?.smartEvidenceSourceEntityID === Entity.Account) {
			dispatch(getAccountById(engagementId, currentDocument?.smartEvidenceSourceId));
		}
	}, []);
	//#endregion

	const createMissingDocuments = (ids) => {
		dispatch(useCreateEntityForm(engagementId, entities.Estimate, documentTypeIdToBeCreated, taskId, ids))
			.then(() => {})
			.catch((error) => {
				// dispatch(setFormV2ToasterVisibility(true, 'error', error.message));
			})
			.finally(() => {
				onCreationFinished();
			});
	};

	const getObjectIds = () => {
		const ids =
			validations?.length > 0
				? validations?.filter((t) => t.validationTypeId === validationTypeId)[0]?.children[0]?.children[0]?.children[0]
						?.uniqueIdList
				: [];
		return ids;
	};

	const onCreationFinished = () => {
		getEstimatesForBody();
		estimateDocumentDeleted();
	};

	const getValidations = () => {
		dispatch(
			getDocumentValidations(
				engagementId,
				[{id: documentId, documentTypeId: documentTypeId}],
				formBodyType.EstimateListing,
				formBodyType.EstimateListing,
				validationGroup.GuidedWorkFlow
			)
		);
	};

	const estimateDocumentDeleted = () => {
		const isEmbeddedDocumentsBodyType = bodyTypesWithEmbeddedDocumentTypes
			.map((item) => item.bodyTypeId)
			.includes(formBodyTypeId);
		dispatch(
			getDocumentValidations(
				engagementId,
				[{id: documentId, documentTypeId: documentTypeId}],
				isEmbeddedDocumentsBodyType ? formBodyTypeId : undefined,
				formBodyTypeId,
				isEmbeddedDocumentsBodyType ? ValidationGroup.GuidedWorkFlow : undefined
			)
		);
	};

	const relateOrUnRelateEstimateToAccount = (assertion) => {
		dispatch(updateStoreAccountRelation(assertion));
	};
	//#endregion

	return (
		<React.Fragment>
			<Fetch fetch={fetch}>
				<StyledBodyEstimateListing className={cardTypeId === CardTypes.CardView ? 'cardViewType5' : ''}>
					{isLoading ? (
						<Loader show label={labels.saving} view="inlineView" styleName="LoadingIndicator" />
					) : (
						<>
							<AresSpecialBodyContainer
								bodyContent={bodyContent}
								headerId={headerId}
								sectionId={sectionId}
								bodyId={bodyId}
								updateBodyOptions={updateBodyOptions}
								getBodiesBySectionId={getBodiesBySectionId}
								isSnapshotView={isSnapshotView}
								showGuidance={showGuidance}
								guidanceFilter={filterGuidanceTypes}
								showDescription={false}
							>
								<section className="AccountFilterWrapper">
									<section className="AccountsSearchbox">
										<Search
											id="AccountsSearchbox"
											styleName="AccountsSearchWrapper"
											value={searchText}
											placeholder={labels.placeholderForSearch}
											searchHoverText={labels.placeholderForSearch}
											clearHoverText={labels.clearHoverText}
											onChange={onChangeSearch}
											onSearchButtonClick={onSearch}
											onEnter={onSearch}
											onClear={onSearchClear}
										/>
									</section>
									<section className="LinksWrapper">
										<Button
											variant="text-alt"
											id="new-estimate"
											onClick={() => setShowCreateEstimateModal(!showCreateEstimateModal)}
											showLabel
											isEllipsed
											buttonLabel={currentResource.CreateEditEstimateLabels.createModalTitle}
										/>
										<span className="LinkSeparator">|</span>
										{
											<section className="ManageEstimateBtn">
												<ManageEstimates onClose={() => getEstimatesForBody()} />
											</section>
										}
										{validateShowRelateEstimateLink && (
											<>
												<span className="LinkSeparator">|</span>
												<Button
													variant="text-alt"
													id="manage-estimate"
													onClick={() => setShowRelateEstimateModal(!showRelateEstimateModal)}
													showLabel
													isEllipsed
													buttonLabel={labels.relateEstimateLink}
												/>
											</>
										)}
									</section>
								</section>{' '}
								{estimates?.data?.length > 0 ? (
									<React.Fragment>
										<section className="EstimateOverviewContainer customScrollbar">
											{/* {isValidationFirstCall && renderCreateMissingEstimates()} */}
											{estimates?.data.map((estimate) => {
												return (
													<EstimateList
														key={estimate.id}
														estimate={estimate}
														documentTypeId={documentTypeId}
														engagementId={engagementId}
														engagement={engagement}
														body={bodyContent}
														onCreateDocument={onCreateDocument}
													/>
												);
											})}
										</section>
										<section className="paging">
											<Pagination
												className="account-paging"
												optionsPerPage={estimateListingPagingOptions.options}
												page={paging.currentPage}
												pageSize={paging.pageSize}
												pageCount={paging.totalPages}
												onPageSizeChanged={handleOnPageSizeChange}
												onPageChanged={handleOnPageChange}
											/>
										</section>
									</React.Fragment>
								) : (
									<section className="EstimateOverviewContainer noEstimateRecords">{noRecordLabel}</section>
								)}
							</AresSpecialBodyContainer>

							{showCreateEstimateModal && (
								<CreateEditEstimateModalWrapper
									isCreate
									engagementId={Number(engagementId)}
									showModal={showCreateEstimateModal}
									setShowModal={setShowCreateEstimateModal}
									onClose={onCloseEditEstimatesModal}
									isManageEntities={false}
								/>
							)}
							{showRelateEstimateModal && (
								<RelateEstimateToAccount
									show={showRelateEstimateModal}
									onHide={onHideRelateEstimates}
									onClose={() => getEstimatesForBody()}
									handleCheckChange={relateOrUnRelateEstimateToAccount}
								/>
							)}
						</>
					)}
				</StyledBodyEstimateListing>
			</Fetch>
		</React.Fragment>
	);
}

const StyledBodyEstimateListing = styled.section`
	position: relative;
	width: 100%;
	height: 100%;
	min-height: var(--px-100);
	&.cardViewType5 {
		.SectionBodyWrapper .AresSectionBodyDescription {
			padding-top: var(--px-40) !important;
		}
	}
	.LoadingIndicator {
		width: 100% !important;
		height: 100% !important;
	}
	.AccountFilterWrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: var(--px-24);
		.AccountFilter {
			margin-right: var(--px-20);
			button {
				border-radius: var(--px-8);
				border-color: var(--neutrals-300);
				width: var(--px-54) !important;
				height: var(--px-54) !important;
				background-color: var(--neutrals-00white);
				svg {
					fill: var(--neutrals-900);
					path {
						fill: var(--neutrals-900);
					}
				}
			}
		}
		.AccountsSearchbox {
			.AccountsSearchWrapper {
				width: var(--px-320);
				.motif-form-field {
					height: 100% !important;
				}
				.motif-input {
					height: var(--px-54) !important;
				}
			}
		}
		.LinksWrapper {
			display: flex;
			align-items: center;
			.ManageAccountBtn {
				display: flex;
				align-items: center;
				width: auto;
				max-width: var(--px-220);
				height: 100%;
			}
			.LinkSeparator {
				margin: 0 var(--px-10);
				color: var(--blue-600);
				font-weight: bold;
				line-height: var(--px-22);
			}
		}
	}
	.EstimateOverviewContainer {
		display: flex;
		flex-direction: column;
		width: 100%;
		min-height: var(--px-150);
		height: auto;
		max-height: var(--px-3000);
		overflow-y: auto;
		padding-right: var(--px-5);
		margin-bottom: var(--px-24);
	}
	.paging {
		display: flex;
		align-items: center;
		margin-left: var(--px-20);
		justify-content: flex-end;
		.pagingShowtext {
			margin-left: auto;
			font-size: var(--px-14);
		}
		.StyledPagination {
			display: flex;
			justify-content: flex-end;
			align-items: center;
			margin-left: var(--px-10);
		}
	}
	.noEstimateRecords {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		border-radius: var(--px-12);
		border: var(--px-1) solid var(--neutrals-200);
		background: var(--neutrals-00white);
		color: var(--neutrals-700);
		font-size: var(--px-14);
		font-weight: 300;
		line-height: var(--px-24);
		margin-bottom: var(--px-16);
		min-height: var(--px-184);
	}
	.LinkSeparator {
		margin: 0 var(--px-10);
		color: var(--blue-600);
		font-weight: bold;
		line-height: var(--px-22);
	}
`;
